import { Injectable } from '@angular/core';
import { AppStore, getNavigationState, getUserProfile } from '@alfresco/aca-shared/store';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { ExtensionService, NavigationState, NodePermissions, ProfileState, RuleContext, RuleEvaluator, SelectionState } from '@alfresco/adf-extensions';
import { Person, PeopleApi, MinimalNodeEntity, NodeEntry, RepositoryInfo, SearchRequest, NodesApi } from '@alfresco/js-api';
import { BehaviorSubject, Observable } from 'rxjs';
import { AlfrescoApiService, AppConfigService, TranslationService, DataColumnComponent, UserPreferencesService } from '@alfresco/adf-core';
import { SMTConfirmDialogComponent } from '../dialogs/confirm/confirm.dialog';
import { ReloadCustomListTableAction } from '../store/actions/alden.actions';
import { ContentManagementService } from 'app/src/app/services/content-management.service';
import { MatDialog } from '@angular/material/dialog';
import { ACCEPT_LANGUAGE_HEADER } from '../models/alden-constants';
import { ContentApiService, NodePermissionService } from '@alfresco/aca-shared';
import { USE_HASH_STRATEGY } from 'projects/aca-testing-shared/src/configs';
import { ShareDataTableAdapter, ShareDataRow } from '@alfresco/adf-content-services';
import { TranslateService } from '@ngx-translate/core';
import { WatermarkParams } from '../models/watermark-params';

@Injectable({
  providedIn: 'root'
})
export class AldenService {

  personDetails: Person;
  personProps: any;
  profile$: Observable<ProfileState>;
  profile: ProfileState;
  navigation$: Observable<NavigationState>;
  navigation: NavigationState;

  showHeader$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  constructor(
    private router: Router,
    private store: Store<AppStore>,
    private alfrescoApiService: AlfrescoApiService,
    private contentService: ContentManagementService,
    private translationService: TranslationService,
    private dialog: MatDialog,
    private appConfigService: AppConfigService,
    private contentApiService: ContentApiService,
    protected extensionService: ExtensionService,
    private nodePermissionService: NodePermissionService,
    public translate: TranslateService,
    private userPreferencesService: UserPreferencesService
  ) {
    this.profile$ = this.store.select(getUserProfile);
    this.navigation$ = this.store.select(getNavigationState);
  }

  /**
   * Obtiene el estado del perfil del usuario.
   *
   * Si el perfil aún no está cargado, se suscribe al observable `profile$`
   * y resuelve la promesa cuando se recibe un perfil válido (con `id`).
   * Si ya está disponible en memoria, lo devuelve directamente.
   *
   * En caso de error en la suscripción, la promesa se rechaza.
   *
   * @returns Promise con el estado del perfil del usuario.
   */
  getProfileState() {
    return new Promise<ProfileState>((resolve, reject) => {
      if (!this.profile) {
        this.profile = {} as ProfileState;
        this.profile$.subscribe( (profile) => {
            if (profile?.id) {
              this.profile = profile;
              resolve(this.profile);
            }
          }, (err) => {
            this.profile = null;
            reject(err);
          }
        );
      } else {
        resolve(this.profile);
      }
    });
  }

  /**
   * Obtiene el estado de navegación de la aplicación.
   *
   * Si el estado aún no está cargado, se suscribe al observable `navigation$`
   * y resuelve la promesa cuando se recibe un valor. Si ya está disponible
   * en memoria, lo devuelve directamente.
   *
   * En caso de error en la suscripción, la promesa se rechaza.
   *
   * @returns Promise con el estado de navegación.
   */
  getNavigationState() {
    return new Promise<NavigationState>((resolve, reject) => {
      if (!this.navigation) {
        this.navigation = {} as NavigationState;
        this.navigation$.subscribe( (navigation) => {
            this.navigation = navigation;
            resolve(this.navigation);
          }, (err) => {
            this.navigation = null;
            reject(err);
          }
        );
      } else {
        resolve(this.navigation);
      }
    });
  }

  /**
   * Obtiene los detalles completos del usuario autenticado.
   *
   * Si `force` es true o no existe información previa en memoria,
   * realiza una llamada al API de Alfresco (`PeopleApi`) para obtener
   * los datos del usuario actual (`-me-`). El resultado se almacena
   * en memoria para futuras consultas.
   *
   * Si ya existe información en memoria y no se fuerza la recarga,
   * devuelve los datos almacenados.
   *
   * @param force (Opcional) Fuerza la recarga de los datos del usuario.
   * @param opts (Opcional) Parámetros adicionales para la llamada al API.
   * @returns Promise con los datos del usuario.
   */
  getPersonDetails(force?: boolean, opts?: any) {
    const peopleApi = new PeopleApi(this.alfrescoApiService.getInstance());
    return new Promise<Person>((resolve, reject) => {
      if (!this.personDetails || force) {
        this.personDetails = new Person();
        peopleApi.getPerson('-me-', opts).then((userInfo) => {
          this.personDetails = userInfo?.entry;
          resolve(this.personDetails);
        }).catch((err) => {
          this.personDetails = null;
          reject(err);
        })
      } else {
        resolve(this.personDetails);
      }
    });
  }

  /**
   * Obtiene un nodo de Alfresco por su identificador.
   *
   * Este método valida que se haya proporcionado un `nodeId` y, en caso contrario,
   * lanza un error explícito. Si el identificador es válido, delega la operación
   * en `contentApiService.getNode`, devolviendo la promesa resultante.
   *
   * @param nodeId Identificador del nodo en Alfresco.
   * @returns Promise con la información del nodo solicitado.
   * @throws Error si `nodeId` es obligatorio.
   */
  async getNode(nodeId: string) {
    if (!nodeId)
      throw new Error("ERROR: nodeId is mandatory.");
    return await this.contentApiService.getNode(nodeId).toPromise();
  }

  /**
   * Solicita confirmación al usuario y elimina los nodos seleccionados.
   *
   * Este método abre un cuadro de diálogo de confirmación antes de proceder
   * con la eliminación de los nodos indicados. Si el usuario confirma la acción,
   * se llama a `contentService.deleteNodes` para eliminar los elementos y se
   * dispara la acción `ReloadCustomListTableAction` para refrescar la tabla.
   *
   * @param items Lista de nodos mínimos a eliminar.
   */
  deleteNode(items: MinimalNodeEntity[]) {
    const dialogRef = this.dialog.open(SMTConfirmDialogComponent, {
      data: {
        title: 'ALDEN.DIALOGS.DELETE_ELEMENTS.TITLE',
        message: 'ALDEN.DIALOGS.DELETE_ELEMENTS.MESSAGE',
        htmlContent: this.translationService.instant(items.length === 1 ? 'ALDEN.DIALOGS.DELETE_ELEMENTS.ELEMENT_MESSAGE' : 'ALDEN.DIALOGS.DELETE_ELEMENTS.MESSAGE', { number: items.length }),
        noLabel: 'ALDEN.DIALOGS.DELETE_ELEMENTS.NO_LABEL',
        yesLabel: 'ALDEN.DIALOGS.DELETE_ELEMENTS.YES_LABEL',
      },
      minWidth: '250px'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.contentService.deleteNodes(items);
        this.store.dispatch(new ReloadCustomListTableAction(false, 2))
      }
    });
  }

  /**
   * Obtiene una propiedad de la configuración de la aplicación.
   *
   * Este método actúa como un wrapper del `appConfigService`, permitiendo
   * recuperar valores configurados en el archivo de configuración de la app.
   * Si la propiedad no existe, devuelve el valor por defecto indicado.
   *
   * @param property Nombre de la propiedad a obtener.
   * @param defaultValue (Opcional) Valor por defecto si la propiedad no existe.
   * @returns Valor de la propiedad solicitada.
   */
  getAppConfigProp(property: string, defaultValue?: any): any {
    return this.appConfigService.get(property, defaultValue);
  }

  /**
   * Abre un nodo en el visor de contenido en una nueva pestaña.
   *
   * Construye la URL del visor utilizando el `uuid` del nodo y añade
   * como parámetro la ubicación actual para permitir volver al punto
   * de origen. Respeta la estrategia de rutas (`hash` o estándar).
   *
   * @param uuid Identificador del nodo a visualizar.
   */
  viewNode(uuid: string) {
    const currentLocation = this.router.url;
    const url = (USE_HASH_STRATEGY ? '/#' : '') + `/view/(viewer:${uuid})?location=${currentLocation}`;
    window.open(url, '_blank');
  }

  /**
   * Descarga un archivo a partir de un Blob recibido desde el backend.
   *
   * Crea un objeto `File` a partir del Blob, genera una URL temporal y
   * simula un clic en un enlace invisible para iniciar la descarga.
   *
   * @param blob Contenido del archivo en formato Blob.
   * @param fileName Nombre con el que se descargará el archivo.
   */
  downloadResponseBlob(blob: Blob, fileName: string) {
    const file = new File([blob], fileName, { type: blob.type });
    const url = window.URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
  }

  // -----------------------------
  // CONTEXT
  // -----------------------------

  /**
   * Construye y devuelve el contexto de reglas asociado a un nodo.
   *
   * Este método prepara la información necesaria para evaluar reglas
   * dinámicas sobre un nodo, incluyendo permisos, evaluadores y datos
   * adicionales requeridos por el motor de extensiones.
   *
   * @param nodeEntry Nodo sobre el que se generará el contexto.
   * @returns Promise con el contexto de reglas generado.
   */
  async getRuleContext(nodeEntry: NodeEntry): Promise<RuleContext> {
    const selectionState = this.getSelectionState(nodeEntry);
    const navigationState: NavigationState = await this.getNavigationState();
    const profileState: ProfileState = await this.getProfileState();
    const nodePermissions: NodePermissions = {
      check: this.check
    };

    const ruleContext: RuleContext = {
      repository: new RepositoryInfo,
      auth: undefined,
      selection: selectionState,
      navigation: navigationState,
      profile: profileState,
      permissions: nodePermissions,
      getEvaluator: this.getEvaluator
    };
    return ruleContext;
  }

  /**
   * Obtiene un evaluador de reglas a partir de su clave.
   *
   * Delegado en el `extensionService`, este método permite recuperar
   * evaluadores personalizados que implementan lógica condicional
   * utilizada en reglas dinámicas.
   *
   * @param key Identificador del evaluador.
   * @returns Instancia del evaluador correspondiente.
   */
  private getEvaluator = (key: string): RuleEvaluator => {
    return this.extensionService.getEvaluator(key);
  };

  /**
   * Verifica si el origen indicado cumple los permisos requeridos.
   *
   * Este método actúa como wrapper del `nodePermissionService`,
   * permitiendo comprobar permisos sobre nodos u otros recursos
   * dentro del contexto de evaluación de reglas.
   *
   * @param source Objeto sobre el que se evaluarán los permisos.
   * @param permissions Lista de permisos requeridos.
   * @param options (Opcional) Configuración adicional para la evaluación.
   * @returns true si cumple los permisos, false en caso contrario.
   */
  private check = (source: any, permissions: string[], options?: any): boolean => {
    return this.nodePermissionService.check(source, permissions, options);
  };

  /**
   * Obtiene las acciones de contexto disponibles para una celda de la tabla.
   *
   * Este método construye el contexto necesario a partir de la fila, columna
   * y adaptador de datos recibidos, y delega en el sistema de extensiones la
   * obtención de las acciones dinámicas que deben mostrarse para ese nodo.
   *
   * @param context Objeto con la información de la tabla, la fila y la columna.
   * @returns Promise con la lista de acciones de contexto aplicables.
   */
  async getContextActions(context: { data: ShareDataTableAdapter, row: ShareDataRow, col: DataColumnComponent }) {
    const contextMenu = this.extensionService.features["contextMenu"] || [];
    const actions = await Promise.all(
      contextMenu.map(async action => {
        try {
          const ruleContext = await this.getRuleContext(context.row.node);
          const isVisible = !action.rules || !action.rules.visible || action.rules.visible == "true" || this.extensionService.evaluateRule(action.rules.visible, ruleContext);
          return isVisible ? action : null;
        } catch (error) {
          //console.debug("Error en la acción: " + action.id, error);
          return null;
        }
      })
    );

    return this.trimSeparators(actions.filter(action => action !== null));
  }

  /**
   * Limpia la lista de acciones eliminando separadores innecesarios.
   *
   * Este método:
   *  - Elimina separadores al inicio y al final de la lista.
   *  - Evita separadores duplicados consecutivos.
   *  - Devuelve una lista de acciones más limpia y adecuada para mostrar en menús.
   *
   * @param actions Lista original de acciones, incluyendo posibles separadores.
   * @returns Lista de acciones sin separadores redundantes.
   */
  private trimSeparators(actions: any[]) {
    let start = 0;
    let end = actions.length - 1;

    // Elimina separadores al principio
    while (start <= end && actions[start].type === "separator") {
      start++;
    }

    // Elimina separadores al final
    while (end >= start && actions[end].type === "separator") {
      end--;
    }

    // Recorta el array
    const trimmed = actions.slice(start, end + 1);

    // Filtra separadores duplicados consecutivos
    const result = [];
    let prevWasSeparator = false;

    for (const action of trimmed) {
      if (action.type === "separator") {
        if (!prevWasSeparator) {
          result.push(action);
          prevWasSeparator = true;
        }
      } else {
        result.push(action);
        prevWasSeparator = false;
      }
    }

    return result;
  }

  /**
   * Construye el estado de selección para un único nodo.
   *
   * Este método genera un objeto `SelectionState` consistente con el formato
   * utilizado por los componentes de selección, indicando:
   *  - El número de elementos seleccionados.
   *  - El nodo seleccionado.
   *  - Si es carpeta o archivo.
   *
   * @param nodeEntry Nodo seleccionado.
   * @returns Estado de selección basado en el nodo proporcionado.
   */
  getSelectionState(nodeEntry: NodeEntry): SelectionState {
    return {count: 1, nodes: [nodeEntry], libraries: [], isEmpty: false, first: nodeEntry, last: nodeEntry,
      folder: nodeEntry?.entry.isFolder ? nodeEntry : undefined,  file: nodeEntry?.entry.isFile ? nodeEntry : undefined };
  }

  // -----------------------------
  // contentClient.callApi
  // -----------------------------

  /**
   * Realiza una llamada genérica al endpoint clásico de Alfresco (`alfresco/service`).
   *
   * Este método actúa como un wrapper simplificado para `callAlfrescoApi`,
   * permitiendo realizar peticiones a los servicios tradicionales de Alfresco
   * sin repetir la construcción de la URL base.
   *
   * @param path Ruta del endpoint dentro de `alfresco/service`.
   * @param httpMethod Método HTTP a utilizar.
   * @param queryParams Parámetros de consulta opcionales.
   * @param headerParams Cabeceras adicionales.
   * @param formParams Parámetros de formulario.
   * @param bodyParam Cuerpo de la petición.
   * @param contentTypes Tipos de contenido aceptados.
   * @param accepts Tipos de respuesta aceptados.
   * @param returnType Tipo de retorno esperado.
   * @returns Promise con la respuesta de la API.
   */
  callAlfresco(path: string, httpMethod: string, queryParams?: Record<string, string>, headerParams?: Record<string, string>, formParams?: any,
    bodyParam?: any, contentTypes?: string[], accepts?: string[], returnType?: string): Promise<any> {
    const url = 'alfresco/service';
    return this.callAlfrescoApi(path, httpMethod, queryParams, headerParams, formParams, bodyParam, contentTypes, accepts, returnType, url);
  }

  /**
   * Ejecuta una búsqueda en el API público de Alfresco Search.
   *
   * Este método envía una petición POST al endpoint oficial de búsqueda
   * (`/public/search/versions/1/search`) utilizando un objeto `SearchRequest`.
   *
   * @param bodyParam Objeto de búsqueda según el modelo `SearchRequest`.
   * @param queryParams Parámetros de consulta opcionales.
   * @param headerParams Cabeceras adicionales.
   * @param formParams Parámetros de formulario.
   * @param contentTypes Tipos de contenido aceptados.
   * @param accepts Tipos de respuesta aceptados.
   * @param returnType Tipo de retorno esperado.
   * @returns Promise con los resultados de la búsqueda.
   */
  callAlfrescoSearchApi(bodyParam: SearchRequest, queryParams?: Record<string, string>, headerParams?: Record<string, string>, formParams?: Record<string, string>,
    contentTypes?: string[], accepts?: string[], returnType?: string): Promise<any> {
    const path = "search";
    const httpMethod = 'POST';
    const url = "alfresco/api/-default-/public/search/versions/1";
    return this.callAlfrescoApi(path, httpMethod, queryParams, headerParams, formParams, bodyParam, contentTypes, accepts, returnType, url);
  }

  /**
   * Realiza una llamada al API Core de Alfresco (`public/alfresco/versions/1`).
   *
   * Este método permite interactuar con los endpoints modernos del Core API,
   * como nodos, sitios, personas, permisos, etc., delegando la llamada en
   * `callAlfrescoApi` con la URL base correspondiente.
   *
   * @param path Ruta del endpoint dentro del Core API.
   * @param httpMethod Método HTTP a utilizar.
   * @param queryParams Parámetros de consulta opcionales.
   * @param headerParams Cabeceras adicionales.
   * @param formParams Parámetros de formulario.
   * @param bodyParam Cuerpo de la petición.
   * @param contentTypes Tipos de contenido aceptados.
   * @param accepts Tipos de respuesta aceptados.
   * @param returnType Tipo de retorno esperado.
   * @returns Promise con la respuesta de la API.
   */
  callAlfrescoCoreApi(path: string, httpMethod: string, queryParams?: Record<string, string>, headerParams?: Record<string, string>,
    formParams?: Record<string, string>, bodyParam?: any, contentTypes?: string[], accepts?: string[], returnType?: string): Promise<any> {
    const url = "alfresco/api/-default-/public/alfresco/versions/1";
    return this.callAlfrescoApi(path, httpMethod, queryParams, headerParams, formParams, bodyParam, contentTypes, accepts, returnType, url);
  }

  /**
   * Método genérico para realizar llamadas al cliente `contentClient` de Alfresco.
   *
   * Este método centraliza la construcción de parámetros y cabeceras para
   * cualquier llamada al API de Alfresco, ya sea al API clásico, al Core API
   * o al Search API. Si no se proporcionan cabeceras, se añade automáticamente
   * el encabezado `Accept-Language` con el locale actual.
   *
   * @param path Ruta del endpoint.
   * @param httpMethod Método HTTP a utilizar.
   * @param queryParams Parámetros de consulta.
   * @param headerParams Cabeceras HTTP.
   * @param formParams Parámetros de formulario.
   * @param bodyParam Cuerpo de la petición.
   * @param contentTypes Tipos de contenido enviados.
   * @param accepts Tipos de contenido aceptados.
   * @param returnType Tipo de retorno esperado.
   * @param url URL base del servicio.
   * @returns Promise con la respuesta de la API.
   */
  callAlfrescoApi(path: string, httpMethod: string, queryParams?: Record<string, string>, headerParams?: Record<string, string>,
    formParams?: Record<string, string>, bodyParam?: any, contentTypes?: string[], accepts?: string[], returnType?: string, url?: string): Promise<any> {

    // Headers
    if (!headerParams || Object.keys(headerParams).length === 0)
      headerParams = { [ACCEPT_LANGUAGE_HEADER]: this.locale };

    // No funciona, incluir en el path
    const pathParams: Record<string, string> = {};

    return this.alfrescoApiService
      .getInstance()
      .contentClient.callApi(path, httpMethod, pathParams, queryParams, headerParams, formParams, bodyParam, contentTypes, accepts, returnType, url);
  }

    /**
   * Solicita confirmación al usuario y elimina los nodos seleccionados.
   *
   * Este método abre un cuadro de diálogo de confirmación antes de proceder
   * con la eliminación de los nodos indicados. Si el usuario confirma la acción,
   * se llama a `contentService.deleteNodes` para eliminar los elementos y se
   * dispara la acción `ReloadCustomListTableAction` para refrescar la tabla.
   *
   * @param items Lista de nodos mínimos a eliminar.
   */
  deleteNodeFromID(item: string): Promise<boolean> {
    const dialogRef = this.dialog.open(SMTConfirmDialogComponent, {
      data: {
        title: 'ALDEN.DIALOGS.DELETE_ELEMENTS.TITLE',
        message: 'ALDEN.DIALOGS.DELETE_ELEMENTS.MESSAGE',
        htmlContent: this.translationService.instant('ALDEN.DIALOGS.DELETE_ELEMENTS.ELEMENT_MESSAGE'),
        noLabel: 'ALDEN.DIALOGS.DELETE_ELEMENTS.NO_LABEL',
        yesLabel: 'ALDEN.DIALOGS.DELETE_ELEMENTS.YES_LABEL',
      },
      minWidth: '250px'
    });

    return new Promise((resolve) => {
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          const nodesApi = new NodesApi(this.alfrescoApiService.getInstance());
          nodesApi.deleteNode(item);
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  async getContentOfFile(nodeId: string): Promise<Blob>{
    const nodesApi = new NodesApi(this.alfrescoApiService.getInstance());
    return nodesApi.getNodeContent(nodeId);
  }

  get locale() {
    return this.userPreferencesService.locale;
  }

  /*Descarga un fichero con marca de agua*/
  downloadWatermarkedFile(uuid: string,params:WatermarkParams,allowedDownloadGroups?:string[]){
    const postBody = JSON.stringify({
      watermark: params,
      allowedGroups: allowedDownloadGroups ? allowedDownloadGroups : []
    });
    const queryParams = {};
    const headerParams = {};
    const formParam = {};
    const contentTypes =['application/json']; 
    const accepts = ['application/pdf'];
    const url = `/smtecno/ssm/utils/download/${uuid}`;

    this.getNode(uuid).then(res => {
        let filename = res.entry.name;
        this.callAlfresco(url, 'POST', queryParams, headerParams, formParam, postBody, contentTypes, accepts,'blob').then(res => {
          const url = URL.createObjectURL(res);
          const link = document.createElement('a');

          link.href = url;
          link.download = filename;
          link.click();

          setTimeout(() => URL.revokeObjectURL(url), 1000);
        });
      });
  } 
}
